import { createSelector } from 'reselect'
import params from '../../utils/routing/params'
import { getNestedProp } from '../../utils/ramda'

/**
 * @const selectCurrentUser
 * @param state
 */
const selectCurrentUser = state => state.currentUser.user

/**
 * @const getCurrentUser
 */
const getCurrentUser = createSelector([selectCurrentUser], user => user || null)

const makeGetCurrentUser = () =>
  createSelector([getCurrentUser], user => user)

const getToursAutoStart = createSelector(getCurrentUser, _ => _ ? _.toursAutoStart : [])

const getInterests = createSelector(getCurrentUser, _ => _ ? _.interests : [])

const getInterestsCount = createSelector(getCurrentUser, _ => _ ? _.interests.length : 0)

const getUserId = createSelector(getCurrentUser, user => getNestedProp('_id', user))

const getGender = createSelector(getCurrentUser, _ => _ ? _.profile.gender : 'unknown')

const getInsToken = createSelector(getCurrentUser, _ => _ && _.insToken ? _.insToken : null)

/**
 * @const getIsPerformingRequest
 * @param state
 * @param key The branch of the redux store
 */
const getIsPerformingRequest = (state, key) =>
  state[key].requestStarted && !state[key].requestEnded

/**
 * @const makeGetIsPerformingRequest
 * @description Memoized selector function factory
 */
const makeGetIsPerformingRequest = () =>
  createSelector(
    [getIsPerformingRequest],
    isPerformingRequest => isPerformingRequest,
  )

/**
 * @const getIsRequestSuccessful
 * @param state
 * @param key The branch of the redux store
 */
const getIsRequestSuccessful = (state, key) =>
  state[key].requestStarted && state[key].requestEnded && !state[key].error

/**
 * @const makeGetIsRequestSuccessful
 * @description Memoized selector function factory
 */
const makeGetIsRequestSuccessful = () =>
  createSelector(
    [getIsRequestSuccessful],
    isRequestSuccessful => isRequestSuccessful,
  )

/**
 * @const getFormValues
 * @param form
 * @returns {*}
 */
const getFormValues = form => (form && form.values ? form.values : {})

/**
 * @const makeGetFormValues
 * @description Memoized selector function factory
 */
const makeGetFormValues = () =>
  createSelector([getFormValues], formValues => formValues)

const getReduxFormValues = createSelector(getFormValues, formValues => formValues)

// Selectors for getting personality assessment questions and answers
const personalityAssessmentQuestions = state => state.questions
const personalityAssessmentAnswers = state => state.answers
const hasNextQuestion = state =>
  state.activeQuestionIndex < state.questions.length - 1
const hasPreviousQuestion = state => state.activeQuestionIndex > 0

const getPersonalityAssessmentQuestions = createSelector(
  [personalityAssessmentQuestions],
  questions => questions,
)

const getPersonalityAssessmentAnswers = createSelector(
  [personalityAssessmentAnswers],
  answers => answers,
)

const getHasNextQuestion = createSelector([hasNextQuestion], hasNext => hasNext)

const getHasPreviousQuestion = createSelector(
  [hasPreviousQuestion],
  hasPrevious => hasPrevious,
)

// Routing
const queryStringObject = state =>
  state && state.routing && state.routing.location
    ? params.getParamObjectWithoutArrays(state.routing.location.search)
    : {}


const queryStringSimply = state =>
  state && state.routing && state.routing.location
    ? params.getObjectSimply(state.routing.location.search)
    : {}

const getQueryStringObject = createSelector(
  queryStringObject,
  object => object,
)

const getQueryStringSimply = createSelector(
  queryStringSimply,
  object => object,
)

// Screen size
const lessThan = state => state.browser.lessThan

const isLessThanSmall = createSelector(
  lessThan,
  less => less.Small,
)

const isLessThanMedium = createSelector(
  lessThan,
  less => less.medium,
)

const isLessThanLarge = createSelector(
  lessThan,
  less => less.large,
)

const getReduxBranch = (store, branch = '') => store[branch] || null

const getErrorFromReduxBranch = createSelector(
  getReduxBranch,
  _ => _.error || null,
)

const getInsTokenOrgName = state => state.insToken && state.insToken.result && state.insToken.result.eduFilter

const getSubAdminAllowed = state =>
  (state && state.insToken && state.insToken.result && state.insToken.result.subAdmin_flg) ? state.insToken.result.subAdmin_flg : false

export default {
  makeGetIsPerformingRequest,
  makeGetIsRequestSuccessful,
  makeGetFormValues,
  makeGetCurrentUser,
  getCurrentUser,
  getToursAutoStart,
  getInterests,
  getInterestsCount,
  getGender,
  getInsToken,
  getIsPerformingRequest,
  getIsRequestSuccessful,
  getReduxFormValues,
  getPersonalityAssessmentQuestions,
  getPersonalityAssessmentAnswers,
  getHasNextQuestion,
  getHasPreviousQuestion,
  getQueryStringObject,
  getQueryStringSimply,
  getUserId,
  isLessThanSmall,
  isLessThanMedium,
  isLessThanLarge,
  getErrorFromReduxBranch,
  getInsTokenOrgName,
  getSubAdminAllowed,
}



// WEBPACK FOOTER //
// ./src/store/shared/selectors.js